import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  createCitologia,
  getCitologiaCatalogos,
  getCitologiaDetalle,
  getCitologiaHistorico,
  getPacienteCitologiaPorNumero,
  getPacienteCitologiaPorTipoNumero,
  updateCitologia,
} from "../../api/citologia";
import { fetchDepartments, fetchTowns } from "../../api/locationApi";
import { calculateAge } from "../../utils/ageCalculator";
import useAuth from "../useAuth";

const getEmptyForm = () => ({
  id_citologia: null,
  autoid: "",
  edad_citologia: "",
  creado_por: "",
  tipo_id: "",
  numero_identificacion: "",
  cod_eapb: "",
  eps_nombre: "",
  num_citologias_previas: "",
  fecha_ultima_citologia: "",
  aspecto_cuello_id: "",
  histerectomia_id: "",
  planifica_id: "",
  tamizaje_mama_id: "",
  fecha_toma: "",
  fecha_envio: "",
  fecha_resultado: "",
  resultado_id: "",
  fecha_entrega: "",
  persona_procedimiento_id: "",
  operador_id: "",
  departamento_id: "",
  municipio_id: "",
  direccion_paciente: "",
});

const getEmptyPatient = () => ({
  autoid: "",
  tipo_id: "",
  sexo: "",
  numero_identificacion: "",
  primer_apellido: "",
  segundo_apellido: "",
  primer_nombre: "",
  segundo_nombre: "",
  nombre_completo: "",
  fecha_nacimiento: "",
  direccion: "",
  cod_departamento: "",
  cod_municipio: "",
  departamento: "",
  municipio: "",
  telefono: "",
  correo: "",
  cod_eapb: "",
  eps_nombre: "",
});

export default function useCitologia() {
  const { user } = useAuth();
  const [catalogos, setCatalogos] = useState(null);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [patient, setPatient] = useState(getEmptyPatient);
  const [form, setForm] = useState(getEmptyForm);
  const [departments, setDepartments] = useState([]);
  const [towns, setTowns] = useState([]);
  const lookupKeyRef = useRef("");
  const lookupTimerRef = useRef(null);
  const latestRef = useRef({ form, patient });
  latestRef.current = { form, patient };

  const patientAge = useMemo(() => {
    const referenceDate = isCompleteIsoDate(form.fecha_toma) ? form.fecha_toma : new Date();
    const years = calculateAge(patient.fecha_nacimiento, referenceDate).years;
    return years === "" ? "" : years;
  }, [patient.fecha_nacimiento, form.fecha_toma]);

  const isHisterectomiaSi = useMemo(
    () => isRespuestaBinariaSi(form.histerectomia_id, catalogos?.respuesta_binaria),
    [form.histerectomia_id, catalogos?.respuesta_binaria]
  );

  useEffect(() => {
    if (!isHisterectomiaSi) {
      return;
    }
    setForm((prev) => {
      if (!prev.aspecto_cuello_id && !prev.planifica_id) {
        return prev;
      }
      return {
        ...prev,
        aspecto_cuello_id: "",
        planifica_id: "",
      };
    });
  }, [isHisterectomiaSi]);

  const loadCatalogos = useCallback(async () => {
    try {
      setLoadingCatalogos(true);
      const res = await getCitologiaCatalogos();
      if (!res.success) {
        toast.error(res.message || "No se pudieron cargar los catálogos");
        return;
      }
      setCatalogos(res.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error cargando catálogos de citología");
    } finally {
      setLoadingCatalogos(false);
    }
  }, []);

  const loadHistorico = useCallback(async (autoid) => {
    if (!autoid) {
      setHistorico([]);
      return;
    }

    try {
      setLoadingHistorico(true);
      const res = await getCitologiaHistorico(autoid);
      if (!res.success) {
        setHistorico([]);
        return;
      }
      setHistorico(res.data || []);
    } catch (error) {
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    let isMounted = true;

    const loadDepartments = async () => {
      try {
        setLoadingLocation(true);
        const data = await fetchDepartments();
        if (isMounted) {
          setDepartments(Array.isArray(data) ? data : []);
        }
      } finally {
        if (isMounted) {
          setLoadingLocation(false);
        }
      }
    };

    loadDepartments();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const selectedDepartment = String(form.departamento_id || "").trim();

    if (!selectedDepartment) {
      setTowns([]);
      return undefined;
    }

    const loadTowns = async () => {
      try {
        setLoadingLocation(true);
        const data = await fetchTowns(selectedDepartment);
        if (isMounted) {
          setTowns(Array.isArray(data) ? data : []);
        }
      } finally {
        if (isMounted) {
          setLoadingLocation(false);
        }
      }
    };

    loadTowns();

    return () => {
      isMounted = false;
    };
  }, [form.departamento_id]);

  useEffect(() => {
    const currentTown = String(form.municipio_id || "").trim();
    if (!currentTown || towns.length === 0) {
      return;
    }

    const exactMatch = towns.some((item) => String(item.codigo ?? "").trim() === currentTown);
    if (exactMatch) {
      return;
    }

    const normalizedCurrentTown = normalizeCatalogCode(currentTown);
    const compatibleTown = towns.find(
      (item) => normalizeCatalogCode(item.codigo) === normalizedCurrentTown
    );

    if (compatibleTown) {
      setForm((prev) => ({
        ...prev,
        municipio_id: String(compatibleTown.codigo ?? "").trim(),
      }));
    }
  }, [form.municipio_id, towns]);

  const clearPatient = useCallback(() => {
    setPatient(getEmptyPatient());
    setHistorico([]);
    setForm((prev) => ({
      ...prev,
      id_citologia: null,
      autoid: "",
      cod_eapb: "",
      eps_nombre: "",
      departamento_id: "",
      municipio_id: "",
      direccion_paciente: "",
    }));
  }, []);

  const applyPatient = useCallback((nextPatient, { silent = false } = {}) => {
    const normalizedPatient = {
      ...getEmptyPatient(),
      ...nextPatient,
    };

    const eligibilityError = getCitologiaEligibilityError(normalizedPatient, form.fecha_toma);
    if (eligibilityError) {
      clearPatient();
      toast.warning(eligibilityError);
      return;
    }

    setPatient(normalizedPatient);
    setForm((prev) => ({
      ...(String(prev.autoid || "") === String(normalizedPatient.autoid || "") ? prev : getEmptyForm()),
      autoid: normalizedPatient.autoid || "",
      tipo_id: normalizedPatient.tipo_id || prev.tipo_id || "",
      numero_identificacion: normalizedPatient.numero_identificacion || prev.numero_identificacion || "",
      cod_eapb: normalizedPatient.cod_eapb || "",
      eps_nombre: normalizedPatient.eps_nombre || "",
        departamento_id: normalizedPatient.cod_departamento ? String(normalizedPatient.cod_departamento).trim().padStart(2, "0") : "",
        municipio_id: normalizedPatient.cod_municipio ? String(normalizedPatient.cod_municipio).trim().padStart(3, "0") : "",
        direccion_paciente: normalizedPatient.direccion || "",
    }));

    if (!silent) {
      toast.success("Paciente cargado correctamente");
    }

    if (normalizedPatient.autoid) {
      loadHistorico(normalizedPatient.autoid);
    } else {
      setHistorico([]);
    }
  }, [clearPatient, form.fecha_toma, loadHistorico]);

  const runLookup = useCallback(async (tipoId, numeroIdentificacion) => {
    const numero = (numeroIdentificacion || "").trim();
    const tipo = (tipoId || "").trim();

    if (!numero) {
      clearPatient();
      return;
    }

    const lookupKey = `${tipo}::${numero}`;
    if (lookupKeyRef.current === lookupKey) {
      return;
    }

    lookupKeyRef.current = lookupKey;
    setLoadingLookup(true);

    try {
      const res = tipo
        ? await getPacienteCitologiaPorTipoNumero(tipo, numero)
        : await getPacienteCitologiaPorNumero(numero);

      if (!res.success) {
        toast.error(res.message || "No se pudo consultar el paciente");
        clearPatient();
        return;
      }

      const status = res.data?.status;
      if (status === "single" && res.data?.patient) {
        applyPatient(res.data.patient, { silent: true });
        return;
      }

      if (status === "multiple") {
        const types = Array.isArray(res.data?.types) ? res.data.types.filter(Boolean) : [];
        clearPatient();
        toast.warning(
          `Existe más de un paciente con ese número. Tipos encontrados: ${types.join(", ")}. Busca con tipo y número.`
        );
        return;
      }

      clearPatient();
      toast.info("No se encontró paciente con la identificación ingresada.");
    } catch (error) {
      clearPatient();
      toast.error(error?.response?.data?.message || "Error consultando paciente");
    } finally {
      setLoadingLookup(false);
    }
  }, [applyPatient, clearPatient]);

  useEffect(() => {
    const numero = form.numero_identificacion?.trim?.() || "";
    const tipo = form.tipo_id?.trim?.() || "";

    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }

    if (!numero || numero.length < 4) {
      lookupKeyRef.current = "";
      if (!numero) {
        clearPatient();
      }
      return undefined;
    }

    lookupTimerRef.current = setTimeout(() => {
      runLookup(tipo, numero);
    }, 450);

    return () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, [form.numero_identificacion, form.tipo_id, runLookup, clearPatient]);

  const setField = useCallback((key, value, event) => {
    if (key === "departamento_id") {
      setForm((prev) => ({
        ...prev,
        departamento_id: value,
        municipio_id: prev.departamento_id === value ? prev.municipio_id : "",
      }));
      return;
    }

    if (key === "num_citologias_previas") {
      const normalizedValue = String(value ?? "").trim();
      if (normalizedValue === "") {
        setForm((prev) => ({ ...prev, [key]: "" }));
        return;
      }

      if (!/^\d+$/.test(normalizedValue)) {
        return;
      }

      const parsedValue = Number(normalizedValue);
      if (parsedValue < 1 || parsedValue > 30) {
        return;
      }

      setForm((prev) => ({ ...prev, [key]: normalizedValue }));
      return;
    }

    if (DATE_FIELDS.has(key)) {
      const rawValue = String(value ?? "").trim();
      const { form: currentForm, patient: currentPatient } = latestRef.current;

      if (rawValue === "") {
        if (!event?.target?.validity?.badInput) {
          setForm((prev) => {
            const next = { ...prev, [key]: "" };
            if (key === "fecha_toma") {
              next.fecha_envio = "";
              next.fecha_resultado = "";
              next.fecha_entrega = "";
            } else if (key === "fecha_envio") {
              next.fecha_resultado = "";
              next.fecha_entrega = "";
            } else if (key === "fecha_resultado") {
              next.fecha_entrega = "";
            }
            return next;
          });
        }
        return;
      }

      const prereqError = getDatePrerequisiteError(key, currentForm);
      if (prereqError) {
        toast.error(prereqError);
        if (event?.target) {
          event.target.value = "";
        }
        setForm((prev) => ({ ...prev, [key]: "" }));
        return;
      }

      const year = getIsoYear(rawValue);
      if (year === null || year < 1000) {
        setForm((prev) => ({ ...prev, [key]: rawValue }));
        return;
      }

      const errorMsg = getDateFieldValidationError(key, rawValue, currentForm, currentPatient);
      if (errorMsg) {
        toast.error(errorMsg);
        if (event?.target) {
          event.target.value = "";
        }
        setForm((prev) => ({ ...prev, [key]: "" }));
        return;
      }

      if (key === "fecha_toma" && currentForm.fecha_envio && currentForm.fecha_envio < rawValue) {
        toast.warning("Fecha envío a lab era inferior a la nueva Fecha toma de Muestra y fue limpiada.");
        setForm((prev) => ({
          ...prev,
          fecha_toma: rawValue,
          fecha_envio: "",
          fecha_resultado: "",
          fecha_entrega: "",
        }));
        return;
      }

      if (key === "fecha_envio" && currentForm.fecha_resultado && currentForm.fecha_resultado < rawValue) {
        toast.warning("Fecha resultado era inferior a la nueva Fecha envío a lab y fue limpiada.");
        setForm((prev) => ({
          ...prev,
          fecha_envio: rawValue,
          fecha_resultado: "",
          fecha_entrega: "",
        }));
        return;
      }

      if (key === "fecha_resultado" && currentForm.fecha_entrega && currentForm.fecha_entrega < rawValue) {
        toast.warning("Fecha entrega a paciente era inferior a la nueva Fecha resultado y fue limpiada.");
        setForm((prev) => ({
          ...prev,
          fecha_resultado: rawValue,
          fecha_entrega: "",
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [key]: rawValue }));
      return;
    }

    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateBlur = useCallback((key, event) => {
    if (!DATE_FIELDS.has(key)) {
      return;
    }

    if (event?.target?.validity?.badInput) {
      toast.error("La fecha ingresada no es válida.");
      event.target.value = "";
      setForm((prev) => ({ ...prev, [key]: "" }));
      return;
    }

    const { form: currentForm, patient: currentPatient } = latestRef.current;
    const rawValue = String(currentForm[key] ?? "").trim();
    if (!rawValue) {
      return;
    }

    const errorMsg = getDateFieldValidationError(key, rawValue, currentForm, currentPatient);
    if (errorMsg) {
      toast.error(errorMsg);
      if (event?.target) {
        event.target.value = "";
      }
      setForm((prev) => ({ ...prev, [key]: "" }));
    }
  }, []);

  const loadDetalle = useCallback(async (idCitologia) => {
    try {
      const res = await getCitologiaDetalle(idCitologia);
      if (!res.success || !res.data) {
        toast.error(res.message || "No se pudo cargar el detalle");
        return;
      }

      const row = res.data;
      const nextPatient = {
        autoid: row.autoid || "",
        tipo_id: row.Tipo_id || "",
        sexo: row.Sexo || "",
        numero_identificacion: row.Identificacion || "",
        primer_apellido: row.Ap1 || "",
        segundo_apellido: row.Ap2 || "",
        primer_nombre: row.Nom1 || "",
        segundo_nombre: row.Nom2 || "",
        nombre_completo: [row.Nom1, row.Nom2, row.Ap1, row.Ap2].filter(Boolean).join(" "),
        fecha_nacimiento: row.Fecha_Nto || "",
        direccion: row.direccion || "",
        cod_departamento: row.cod_departamento || "",
        cod_municipio: row.cod_municipio || "",
        departamento: row.departamento || "",
        municipio: row.municipio || "",
        telefono: row.telefono || "",
        cod_eapb: row.cod_eapb || row.paciente_cod_eapb || "",
        eps_nombre: row.eapb_nombre || "",
      };

      const eligibilityError = getCitologiaEligibilityError(nextPatient, row.fecha_toma || form.fecha_toma);
      if (eligibilityError) {
        clearPatient();
        toast.warning(eligibilityError);
        return;
      }

      setPatient(nextPatient);
      setForm({
        ...getEmptyForm(),
        id_citologia: row.id_citologia || null,
        autoid: row.autoid || "",
        edad_citologia: asText(row.edad_citologia),
        creado_por: asText(row.creado_por),
        tipo_id: row.Tipo_id || "",
        numero_identificacion: row.Identificacion || "",
        cod_eapb: row.cod_eapb || row.paciente_cod_eapb || "",
        eps_nombre: row.eapb_nombre || "",
        num_citologias_previas: asText(row.num_citologias_previas),
        fecha_ultima_citologia: asText(row.fecha_ultima_citologia),
        aspecto_cuello_id: asText(row.aspecto_cuello_id),
        histerectomia_id: asText(row.histerectomia_id),
        planifica_id: asText(row.planifica_id),
        tamizaje_mama_id: asText(row.tamizaje_mama_id),
        fecha_toma: asText(row.fecha_toma),
        fecha_envio: asText(row.fecha_envio),
        fecha_resultado: asText(row.fecha_resultado || row.fecha_recepcion),
        resultado_id: asText(row.resultado_id),
        fecha_entrega: asText(row.fecha_entrega),
        persona_procedimiento_id: asText(row.persona_procedimiento_id),
        operador_id: asText(row.operador_id),
        departamento_id: row.cod_departamento ? String(row.cod_departamento).trim().padStart(2, "0") : "",
        municipio_id: row.cod_municipio ? String(row.cod_municipio).trim().padStart(3, "0") : "",
        direccion_paciente: asText(row.direccion),
      });

      if (row.autoid) {
        loadHistorico(row.autoid);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error cargando detalle");
    }
  }, [clearPatient, form.fecha_toma, loadHistorico]);

  const resetForm = useCallback(() => {
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }
    lookupKeyRef.current = "";
    setPatient(getEmptyPatient());
    setHistorico([]);
    setForm(getEmptyForm());
  }, []);

  const submit = useCallback(async (event) => {
    event?.preventDefault?.();

    const resolvedAutoid = form.autoid || patient.autoid;
    if (!resolvedAutoid) {
      toast.error("Debes identificar un paciente válido antes de guardar.");
      return;
    }

    const eligibilityError = getCitologiaEligibilityError(patient, form.fecha_toma);
    if (eligibilityError) {
      toast.error(eligibilityError);
      return;
    }

    if (!form.operador_id) {
      toast.error("Debes seleccionar el operador.");
      return;
    }

    if (form.num_citologias_previas !== "") {
      const numCitologiasPrevias = Number(form.num_citologias_previas);
      if (!Number.isInteger(numCitologiasPrevias) || numCitologiasPrevias < 1 || numCitologiasPrevias > 30) {
        toast.error("N° de Citologías previas debe ser un entero entre 1 y 30.");
        return;
      }
    }

    if (form.fecha_ultima_citologia && form.fecha_ultima_citologia > getTodayIsoDate()) {
      toast.error("Fecha última citología no debe ser superior a la fecha actual.");
      return;
    }

    if (!form.fecha_toma) {
      toast.error("Fecha toma de Muestra es obligatoria.");
      return;
    }

    if (form.fecha_toma > getTodayIsoDate()) {
      toast.error("Fecha toma de Muestra no debe ser superior a la fecha actual.");
      return;
    }

    if (form.fecha_envio) {
      if (form.fecha_envio > getTodayIsoDate()) {
        toast.error("Fecha envío a lab no debe ser superior a la fecha actual.");
        return;
      }

      if (form.fecha_envio < form.fecha_toma) {
        toast.error("Fecha envío a lab debe ser superior o igual a Fecha toma de Muestra.");
        return;
      }
    }

    const hasFechaResultado = Boolean(form.fecha_resultado);
    const hasResultado = Boolean(form.resultado_id);
    const hasPersonaProcedimiento = Boolean(form.persona_procedimiento_id);

    const resultFieldsFilled = [hasFechaResultado, hasResultado, hasPersonaProcedimiento].filter(Boolean).length;
    if (resultFieldsFilled > 0 && resultFieldsFilled < 3) {
      toast.error("Los campos Fecha resultado, Resultado y Persona que realiza el procedimiento deben estar todos diligenciados o todos vacíos.");
      return;
    }

    if (form.fecha_resultado) {
      if (form.fecha_resultado > getTodayIsoDate()) {
        toast.error("Fecha resultado no debe ser superior a la fecha actual.");
        return;
      }

      if (!form.fecha_envio) {
        toast.error("Debes diligenciar Fecha envío a lab antes de Fecha resultado.");
        return;
      }

      if (form.fecha_resultado < form.fecha_envio) {
        toast.error("Fecha resultado debe ser superior o igual a Fecha envío a lab.");
        return;
      }
    }

    if (form.fecha_entrega) {
      if (form.fecha_entrega > getTodayIsoDate()) {
        toast.error("Fecha entrega a paciente no debe ser superior a la fecha actual.");
        return;
      }

      if (!form.fecha_resultado) {
        toast.error("Debes diligenciar Fecha resultado antes de Fecha entrega a paciente.");
        return;
      }

      if (form.fecha_entrega < form.fecha_resultado) {
        toast.error("Fecha entrega a paciente no puede ser inferior a Fecha resultado.");
        return;
      }
    }

    if (form.id_citologia && form.creado_por && user?.id && form.creado_por !== user.id && !user?.es_supersu) {
      toast.error("Solo el usuario que creó el registro puede modificarlo.");
      return;
    }

    if (!form.departamento_id || !form.municipio_id || !form.direccion_paciente.trim()) {
      toast.error("Debes diligenciar departamento, municipio y dirección del paciente.");
      return;
    }

    const payload = {
      citologia: normalizePayload({
        autoid: resolvedAutoid,
        edad_citologia: patientAge === "" ? null : patientAge,
        cod_eapb: form.cod_eapb,
        num_citologias_previas: form.num_citologias_previas,
        fecha_ultima_citologia: form.fecha_ultima_citologia,
        aspecto_cuello_id: isHisterectomiaSi ? "" : form.aspecto_cuello_id,
        histerectomia_id: form.histerectomia_id,
        planifica_id: isHisterectomiaSi ? "" : form.planifica_id,
        tamizaje_mama_id: form.tamizaje_mama_id,
        fecha_toma: form.fecha_toma,
        fecha_envio: form.fecha_envio,
        fecha_resultado: form.fecha_resultado,
        resultado_id: form.resultado_id,
        fecha_entrega: form.fecha_entrega,
        persona_procedimiento_id: form.persona_procedimiento_id,
        operador_id: form.operador_id,
      }),
      paciente_contacto: normalizePayload({
        codDepartamento: form.departamento_id,
        codMunicipio: form.municipio_id,
        direccion: form.direccion_paciente.trim(),
      }),
    };
    try {
      setSaving(true);
      const res = form.id_citologia
        ? await updateCitologia({ id_citologia: form.id_citologia, ...payload })
        : await createCitologia(payload);

      if (!res.success) {
        toast.error(res.message || "No se pudo guardar la citología");
        return;
      }

      toast.success(res.message || "Citología guardada correctamente");

      if (resolvedAutoid) {
        await loadHistorico(resolvedAutoid);
      }

      if (!form.id_citologia && res.data?.id_citologia) {
        await loadDetalle(res.data.id_citologia);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error guardando la citología");
    } finally {
      setSaving(false);
    }
  }, [form, isHisterectomiaSi, loadDetalle, loadHistorico, patient.autoid, patientAge, user?.es_supersu, user?.id]);

  return {
    catalogos,
    form,
    historico,
    departments,
    loadingCatalogos,
    loadingHistorico,
    loadingLocation,
    loadingLookup,
    patient,
    patientAge,
    resetForm,
    saving,
    setField,
    towns,
    loadDetalle,
    submit,
    canEditCurrentRecord: !form.id_citologia || !form.creado_por || !user?.id || form.creado_por === user.id || user?.es_supersu,
    isHisterectomiaSi,
    handleDateBlur,
  };
}

const normalizePayload = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    out[key] = value === "" ? null : value;
  });
  return out;
};

const asText = (value) => {
  if (value === null || typeof value === "undefined") {
    return "";
  }
  return String(value);
};

const getTodayIsoDate = () => {
  const now = new Date();
  const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

const normalizeCatalogCode = (value) => {
  const text = String(value ?? "").trim();
  if (!text) {
    return "";
  }

  const numeric = text.replace(/^0+/, "");
  return numeric || "0";
};

const isRespuestaBinariaSi = (optionId, opciones = []) => {
  const id = String(optionId ?? "").trim();
  if (!id || !Array.isArray(opciones)) {
    return false;
  }

  const found = opciones.find((item) => String(item.id_catalogo ?? "").trim() === id);
  if (!found) {
    return false;
  }

  const normalizedDesc = String(found.descripcion ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^\d+[\s.-]*/, "")
    .trim()
    .toUpperCase();

  const normalizedCode = String(found.codigo ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

  return normalizedDesc === "SI" || normalizedCode === "SI";
};

const DATE_FIELDS = new Set([
  "fecha_ultima_citologia",
  "fecha_toma",
  "fecha_envio",
  "fecha_resultado",
  "fecha_entrega",
]);

const getIsoYear = (value) => {
  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(String(value ?? "").trim());
  if (!match) {
    return null;
  }
  return Number(match[1]);
};

const isCompleteIsoDate = (value) => {
  const text = String(value ?? "").trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

const toIsoDateSlice = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const getDatePrerequisiteError = (key, currentForm) => {
  if (key === "fecha_envio" && !isCompleteIsoDate(currentForm?.fecha_toma)) {
    return "Debes diligenciar Fecha toma de Muestra antes de Fecha envío a lab.";
  }
  if (key === "fecha_resultado" && !isCompleteIsoDate(currentForm?.fecha_envio)) {
    return "Debes diligenciar Fecha envío a lab antes de Fecha resultado.";
  }
  if (key === "fecha_entrega" && !isCompleteIsoDate(currentForm?.fecha_resultado)) {
    return "Debes diligenciar Fecha resultado antes de Fecha entrega a paciente.";
  }
  return "";
};

const getDateFieldValidationError = (key, rawValue, currentForm, currentPatient) => {
  const prereqError = getDatePrerequisiteError(key, currentForm);
  if (prereqError) {
    return prereqError;
  }

  if (!isCompleteIsoDate(rawValue)) {
    return "La fecha ingresada no es válida.";
  }

  const today = getTodayIsoDate();

  if (key === "fecha_ultima_citologia") {
    if (rawValue > today) {
      return "Fecha última citología no debe ser superior a la fecha actual.";
    }
    const birthDate = toIsoDateSlice(currentPatient?.fecha_nacimiento);
    if (isCompleteIsoDate(birthDate) && rawValue < birthDate) {
      return "Fecha última citología no puede ser inferior a la fecha de nacimiento del paciente.";
    }
    return "";
  }

  if (key === "fecha_toma") {
    if (rawValue > today) {
      return "Fecha toma de Muestra no debe ser superior a la fecha actual.";
    }
    if (currentPatient?.autoid) {
      const eligibilityError = getCitologiaEligibilityError(currentPatient, rawValue);
      if (eligibilityError) {
        return eligibilityError;
      }
    }
    return "";
  }

  if (key === "fecha_envio") {
    if (rawValue > today) {
      return "Fecha envío a lab no debe ser superior a la fecha actual.";
    }
    if (rawValue < currentForm.fecha_toma) {
      return "Fecha envío a lab debe ser superior o igual a Fecha toma de Muestra.";
    }
    return "";
  }

  if (key === "fecha_resultado") {
    if (rawValue > today) {
      return "Fecha resultado no debe ser superior a la fecha actual.";
    }
    if (rawValue < currentForm.fecha_envio) {
      return "Fecha resultado debe ser superior o igual a Fecha envío a lab.";
    }
    return "";
  }

  if (key === "fecha_entrega") {
    if (rawValue > today) {
      return "Fecha entrega a paciente no debe ser superior a la fecha actual.";
    }
    if (rawValue < currentForm.fecha_resultado) {
      return "Fecha entrega a paciente no puede ser inferior a Fecha resultado.";
    }
    return "";
  }

  return "";
};

const getCitologiaEligibilityError = (patient, referenceDate) => {
  const sexo = String(patient?.sexo || "").trim().toUpperCase();
  if (sexo !== "F") {
    return "La citología solo aplica para pacientes de sexo F.";
  }

  const edad = calculateAge(patient?.fecha_nacimiento, referenceDate || new Date()).years;
  if (edad === "") {
    return "No fue posible validar la edad del paciente para citología.";
  }

  if (edad !== "" && Number(edad) < 20) {
    return "La citología solo aplica para pacientes con edad mayor o igual a 20 años.";
  }

  return "";
};
